import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Group, Paper, Stack, Text, Loader, List, Button } from "@mantine/core";
import { IconFileText, IconExternalLink } from "@tabler/icons-react";

type EventFileDto = {
    fileID: number;
    eventID: number;
    name: string;
    originalFileName: string;
    sizeBytes: number;
    sortOrder: number;
    downloadUrl: string;
};

function formatBytes(bytes: number) {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const v = bytes / Math.pow(k, i);
    return `${v.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

export default function EventFilesViewPage() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const { eventId } = useParams();
    const eid = Number(eventId);

    const [loading, setLoading] = useState(true);
    const [files, setFiles] = useState<EventFileDto[]>([]);

    async function load() {
        setLoading(true);
        try {
            const res = await fetch(`${apiUrl}/api/events/${eid}/files`);
            if (!res.ok) throw new Error("Load failed");
            const data = await res.json();

            const mapped: EventFileDto[] = (data as any[]).map((x) => ({
                fileID: x.fileID ?? x.FileID,
                eventID: x.eventID ?? x.EventID,
                name: x.name ?? x.Name,
                originalFileName: x.originalFileName ?? x.OriginalFileName,
                sizeBytes: x.sizeBytes ?? x.SizeBytes,
                sortOrder: x.sortOrder ?? x.SortOrder,
                downloadUrl: x.downloadUrl ?? x.DownloadUrl,
            }));

            setFiles(mapped.sort((a, b) => a.sortOrder - b.sortOrder || a.fileID - b.fileID));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!eid || Number.isNaN(eid)) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eid]);

    return (
        <Paper withBorder p="md">
            <Group justify="space-between">
                <Text fw={600}>Dokumenter</Text>
                <Button variant="light" onClick={load} disabled={loading || !eid}>
                    Genindlæs
                </Button>
            </Group>

            {!eid || Number.isNaN(eid) ? (
                <Text mt="md" c="dimmed">Ugyldigt eventId i URL.</Text>
            ) : loading ? (
                <Group mt="md">
                    <Loader size="sm" />
                    <Text>Loader…</Text>
                </Group>
            ) : files.length === 0 ? (
                <Text mt="md" c="dimmed">Ingen dokumenter tilknyttet dette event endnu.</Text>
            ) : (
                <List mt="md" spacing="sm" icon={<IconFileText size={18} />}>
                    {files.map((f) => (
                        <List.Item key={f.fileID}>
                            <Group justify="space-between" wrap="nowrap">
                                <div>
                                    <Text fw={500}>{f.name}</Text>
                                    <Text size="xs" c="dimmed">
                                        {f.originalFileName} · {formatBytes(f.sizeBytes)}
                                    </Text>
                                </div>
                                <Button
                                    variant="light"
                                    rightSection={<IconExternalLink size={16} />}
                                    component="a"
                                    href={`${apiUrl}${f.downloadUrl}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Åbn
                                </Button>
                            </Group>
                        </List.Item>
                    ))}
                </List>
            )}
        </Paper>
    );
}
